import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import LeadPicker from "../components/LeadPicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  Plus, 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  Calendar as CalendarIcon,
  User,
  Target,
  Filter,
  MoreHorizontal,
  Trash2,
  X,
  Loader2
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const priorityColors = {
  high: "bg-red-50 text-red-700 border-red-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-slate-100 text-slate-600 border-slate-200"
};

const statusColors = {
  pending: "bg-slate-100 text-slate-600",
  in_progress: "bg-blue-50 text-blue-700",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-600"
};

const statusLabels = {
  pending: "Pending",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled"
};

export default function TasksPage({ user }) {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    priority: "medium",
    assigned_to: "",
    lead_id: "",
    due_date: null
  });

  // Multi-select state
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchTasks();
    fetchUsers();
    fetchLeads();
  }, [statusFilter, priorityFilter]);

  const fetchTasks = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (priorityFilter !== "all") params.append("priority", priorityFilter);

      const response = await fetch(`${API}/tasks?${params}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setTasks(data.tasks);
      }
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/users`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await fetch(`${API}/leads?limit=100`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads);
      }
    } catch (error) {
      console.error("Failed to fetch leads:", error);
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.title || !newTask.assigned_to) {
      toast.error("Please fill in title and assignee");
      return;
    }

    try {
      const response = await fetch(`${API}/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...newTask,
          due_date: newTask.due_date ? newTask.due_date.toISOString() : null,
          lead_id: newTask.lead_id || null
        })
      });

      if (response.ok) {
        toast.success("Task created");
        setIsAddDialogOpen(false);
        setNewTask({
          title: "",
          description: "",
          priority: "medium",
          assigned_to: "",
          lead_id: "",
          due_date: null
        });
        fetchTasks();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create task");
      }
    } catch (error) {
      toast.error("Failed to create task");
    }
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      const response = await fetch(`${API}/tasks/${taskId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success("Task updated");
        fetchTasks();
      } else {
        toast.error("Failed to update task");
      }
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;

    try {
      const response = await fetch(`${API}/tasks/${taskId}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Task deleted");
        fetchTasks();
      } else {
        toast.error("Failed to delete task");
      }
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  // Multi-select handlers
  const toggleSelectTask = (taskId, e) => {
    if (e) e.stopPropagation();
    setSelectedTasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const toggleSelectAllInColumn = (columnTasks) => {
    const columnIds = columnTasks.map(t => t.task_id);
    const allSelected = columnIds.every(id => selectedTasks.has(id));
    
    setSelectedTasks(prev => {
      const next = new Set(prev);
      if (allSelected) {
        columnIds.forEach(id => next.delete(id));
      } else {
        columnIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedTasks(new Set());
  };

  const handleBulkDeleteTasks = async () => {
    if (selectedTasks.size === 0) return;
    if (!window.confirm(`Delete ${selectedTasks.size} selected task${selectedTasks.size !== 1 ? "s" : ""}?`)) return;

    setDeleting(true);
    try {
      const response = await fetch(`${API}/tasks/bulk-delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ task_ids: Array.from(selectedTasks) })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Deleted ${data.count} task${data.count !== 1 ? "s" : ""}`);
        setSelectedTasks(new Set());
        fetchTasks();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete tasks");
      }
    } catch (error) {
      toast.error("Failed to delete tasks");
    } finally {
      setDeleting(false);
    }
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const formatDueDate = (dueDate) => {
    if (!dueDate) return null;
    const date = new Date(dueDate);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === tomorrow.toDateString()) return "Tomorrow";
    return format(date, "MMM d, yyyy");
  };

  // Group tasks by status
  const groupedTasks = {
    pending: tasks.filter(t => t.status === "pending"),
    in_progress: tasks.filter(t => t.status === "in_progress"),
    completed: tasks.filter(t => t.status === "completed")
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
            Tasks
          </h1>
          <p className="text-slate-500 mt-1">
            {tasks.length} total tasks
          </p>
        </div>

        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-task-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Task
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Create New Task</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label htmlFor="title">Task Title *</Label>
                <Input
                  id="title"
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder="e.g., Follow up call with client"
                  data-testid="input-task-title"
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Add details..."
                  rows={3}
                  data-testid="input-task-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Assign To *</Label>
                  <Select
                    value={newTask.assigned_to}
                    onValueChange={(value) => setNewTask({ ...newTask, assigned_to: value })}
                  >
                    <SelectTrigger data-testid="select-assignee">
                      <SelectValue placeholder="Select person" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.user_id} value={u.user_id}>
                          {u.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select
                    value={newTask.priority}
                    onValueChange={(value) => setNewTask({ ...newTask, priority: value })}
                  >
                    <SelectTrigger data-testid="select-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Link to Lead (Optional)</Label>
                <LeadPicker
                  value={newTask.lead_id || ""}
                  onChange={(leadId) => setNewTask({ ...newTask, lead_id: leadId })}
                  leadsFallback={leads}
                  placeholder="Search by business or contact name…"
                  testId="task-lead-picker"
                />
              </div>
              <div>
                <Label>Due Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="select-due-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {newTask.due_date ? format(newTask.due_date, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={newTask.due_date}
                      onSelect={(date) => setNewTask({ ...newTask, due_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <Button 
                onClick={handleCreateTask} 
                className="w-full"
                data-testid="submit-task-btn"
              >
                Create Task
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Selection Action Bar */}
      {selectedTasks.size > 0 && (
        <div 
          className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg"
          data-testid="task-selection-action-bar"
        >
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="text-sm font-medium">
              {selectedTasks.size} task{selectedTasks.size !== 1 ? "s" : ""} selected
            </Badge>
            <button 
              onClick={clearSelection}
              className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
              data-testid="clear-task-selection-btn"
            >
              <X className="w-3 h-3" />
              Clear
            </button>
          </div>
          <Button 
            size="sm" 
            variant="destructive"
            onClick={handleBulkDeleteTasks}
            disabled={deleting}
            data-testid="bulk-delete-tasks-btn"
          >
            {deleting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4 mr-2" />
            )}
            Delete Tasks
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="filter-status">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>

        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-[160px]" data-testid="filter-priority">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priority</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Project Board */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {Object.entries(groupedTasks).map(([status, statusTasks]) => (
          <div key={status} className="space-y-3">
            <div className="flex items-center justify-between px-2">
              <h3 className="font-heading text-lg font-bold capitalize flex items-center gap-2">
                {status === "pending" && <Clock className="w-4 h-4 text-slate-500" />}
                {status === "in_progress" && <AlertCircle className="w-4 h-4 text-blue-500" />}
                {status === "completed" && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                {statusLabels[status]}
              </h3>
              <div className="flex items-center gap-2">
                {statusTasks.length > 0 && (
                  <Checkbox
                    checked={statusTasks.length > 0 && statusTasks.every(t => selectedTasks.has(t.task_id))}
                    onCheckedChange={() => toggleSelectAllInColumn(statusTasks)}
                    data-testid={`select-all-${status}`}
                  />
                )}
                <Badge variant="secondary">{statusTasks.length}</Badge>
              </div>
            </div>

            <div className="space-y-3 min-h-[200px] bg-slate-50/50 rounded-lg p-3">
              {statusTasks.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No tasks</p>
              ) : (
                statusTasks.map((task) => (
                  <Card 
                    key={task.task_id} 
                    className={`hover:shadow-md transition-shadow cursor-pointer ${
                      isOverdue(task.due_date) && task.status !== "completed" ? "border-red-200" : ""
                    } ${selectedTasks.has(task.task_id) ? "ring-2 ring-primary/30 bg-primary/5" : ""}`}
                    data-testid={`task-card-${task.task_id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedTasks.has(task.task_id)}
                              onCheckedChange={(checked) => toggleSelectTask(task.task_id)}
                              data-testid={`select-task-${task.task_id}`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={`${priorityColors[task.priority]} text-xs border`}>
                                {task.priority}
                              </Badge>
                              {task.lead_name && (
                                <Badge 
                                  variant="outline" 
                                  className="text-xs cursor-pointer"
                                  onClick={() => navigate(`/leads/${task.lead_id}`)}
                                >
                                  <Target className="w-3 h-3 mr-1" />
                                  {task.lead_name}
                                </Badge>
                              )}
                            </div>
                            <h4 className="font-medium text-slate-900 truncate">{task.title}</h4>
                            {task.description && (
                              <p className="text-sm text-slate-500 mt-1 line-clamp-2">{task.description}</p>
                            )}
                            <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {task.assigned_to_name}
                              </span>
                              {task.due_date && (
                                <span className={`flex items-center gap-1 ${
                                  isOverdue(task.due_date) && task.status !== "completed" ? "text-red-600 font-medium" : ""
                                }`}>
                                  <CalendarIcon className="w-3 h-3" />
                                  {formatDueDate(task.due_date)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {task.status !== "in_progress" && (
                              <DropdownMenuItem onClick={() => handleUpdateStatus(task.task_id, "in_progress")}>
                                Mark In Progress
                              </DropdownMenuItem>
                            )}
                            {task.status !== "completed" && (
                              <DropdownMenuItem onClick={() => handleUpdateStatus(task.task_id, "completed")}>
                                Mark Complete
                              </DropdownMenuItem>
                            )}
                            {task.status !== "pending" && (
                              <DropdownMenuItem onClick={() => handleUpdateStatus(task.task_id, "pending")}>
                                Mark Pending
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => handleDeleteTask(task.task_id)}
                            >
                              Delete Task
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
